import { Divider, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedUniqueValues,
	useTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/table-core';
import camelCase from 'lodash-es/camelCase';
import { useCallback } from 'react';

import { AddIcon, DownloadIcon, Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createFilterOnlyColumn } from '@apple/ui/data-table/utils/filters';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { ExcelUploadDialog } from '@apple/utils/files/components/ExcelUploadDialog';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedBoolean } from '@apple/utils/globalization/components/FormattedBoolean';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import {
	deleteRecurringOrders,
	getRecurringOrderDataTableQueryOptions,
	getRecurringOrdersTemplateUrl,
	saveRecurringOrders,
	uploadRecurringOrdersExcelFile,
} from '@apple/features/order-retail/api/recurring';
import type {
	RecurringOrderEntity,
	RecurringOrderFilters,
	RecurringOrderListing,
} from '@apple/features/order-retail/models/recurring';
import { recurringOrderFrequencySchema } from '@apple/features/order-retail/models/recurring';
import { recurringOrderQueryKeys } from '@apple/features/order-retail/queries/recurring';

const features = createDataTableFeatures({
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/orders/recurring')({
	component: RecurringOrderListRoute,
});

const defaultFilters: RecurringOrderFilters = {};
const defaultSorting: SortingState = [{ id: 'storeId', desc: true }];

function RecurringOrderListRoute() {
	const { t } = useTranslation('order-recurring');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const { auth, queryClient } = Route.useRouteContext();
	const [uploadOpen, uploadActions] = useDisclosure(false);
	const excelTemplate = useFileDownload({
		method: 'get',
		url: getRecurringOrdersTemplateUrl(),
		fallbackFilename: 'recurring-orders.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	const tableState = useTableState<RecurringOrderListing, RecurringOrderFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const [recurringOrderQuery, countriesQuery] = useQueries({
		queries: [
			getRecurringOrderDataTableQueryOptions<RecurringOrderFilters>({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
				sorting: tableState.state.sorting,
			}),
			globalizationQueryOptions.countries,
		],
	});

	const table = useTable({
		features,
		data: recurringOrderQuery.data.rows,
		rowCount: recurringOrderQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'frequencyCode':
						return new Map(recurringOrderFrequencySchema.options.map(x => [x, 1]));
					case 'countryCode':
						return new Map(countriesQuery.data?.map(x => [x.key, 1]));
					case 'isActive':
						return new Map([undefined, true, false].map(x => [x, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'orderNumber',
				enableColumnFilter: true,
				header: t('fields.orderNumber'),
				filter: {
					group: t('filterGroups.details'),
				},
				cell: ({ row }) => (
					<Link
						to='/orders/recurring/$id'
						state={{ from: location }}
						params={{ id: row.original.id.toString() }}
						title={t('links.view')}
					>
						<Text size='sm'>{row.original.orderNumber}</Text>
					</Link>
				),
			},
			{
				accessorKey: 'orderName',
				header: t('fields.orderName'),
				enableColumnFilter: true,
				filter: {
					group: t('filterGroups.details'),
				},
			},
			{
				accessorKey: 'storeId',
				header: t('fields.storeId'),
				enableColumnFilter: true,
				filter: {
					group: t('filterGroups.details'),
				},
			},
			{
				accessorKey: 'isActive',
				header: t('fields.status'),
				enableSorting: false,
				enableColumnFilter: true,
				filter: {
					group: t('filterGroups.details'),
					variant: 'segmented-switch',
					getFilterDisplayValue: x => {
						switch (x) {
							case true:
								return t('labels.active');
							case false:
								return t('labels.inactive');
							default:
								return t('common:label.all');
						}
					},
				},
				cell: ({ row }) => <FormattedBoolean value={row.original.isActive} useIcon />,
			},
			{
				accessorKey: 'orderTotal',
				header: t('fields.orderTotal'),
				enableColumnFilter: false,
			},
			{
				accessorKey: 'frequencyCode',
				header: t('fields.frequency'),
				enableColumnFilter: true,
				filter: {
					group: t('filterGroups.details'),
					variant: 'select',
					getFilterDisplayValue: value =>
						t(`labels.frequency.${camelCase(value as string)}`),
				},
				cell: ({ row }) => row.original.frequency,
			},
			{
				accessorKey: 'nextProcessingDate',
				header: t('fields.nextProcessingDate'),
				enableColumnFilter: false,
				cell: ({ row }) => <FormattedDate value={row.original.nextProcessingDate} />,
			},
			createFilterOnlyColumn<RecurringOrderListing, RecurringOrderFilters>({
				group: t('filterGroups.details'),
				label: t('fields.country'),
				field: 'countryCode',
				variant: 'select',
				getFilterDisplayValue: value =>
					countriesQuery.data?.find(x => x.key === value)?.value ?? String(value),
			}),
		],
	});

	const handleUploadConfirmed = useCallback(async () => {
		await queryClient.resetQueries({
			queryKey: recurringOrderQueryKeys.all,
		});
		await recurringOrderQuery.refetch();
	}, [queryClient, recurringOrderQuery]);

	return (
		<TableLayout
			title={t('title')}
			table={table}
			toolbarButtons={
				!auth.hasPermission('AppleRetail.Security.Features.RecurringOrdersManagement')
					? []
					: [
							<ToolbarButton
								key='add'
								tooltip={t('buttons.add.tooltip')}
								icon={AddIcon}
								onClick={() => {
									void navigate({ to: '/orders/recurring/add' });
								}}
							/>,
							<Divider key='divider' orientation='vertical' mx='xs' />,
							<ToolbarButton
								key='download'
								tooltip={t('buttons.download.tooltip')}
								icon={DownloadIcon}
								loading={excelTemplate.downloading}
								onClick={() => void excelTemplate.download()}
							/>,
							<ToolbarButton
								key='upload'
								tooltip={t('buttons.upload.tooltip')}
								icon={Upload}
								disabled={uploadOpen}
								onClick={uploadActions.open}
							/>,
						]
			}
		>
			<DataTable
				table={table}
				variant='apple-table'
				loading={recurringOrderQuery.isFetching}
			/>
			<ExcelUploadDialog<RecurringOrderEntity, 'orderNumber'>
				keyProperty={'orderNumber'}
				keyLabel={t('fields.orderNumber')}
				opened={uploadOpen}
				close={uploadActions.close}
				onUpload={uploadRecurringOrdersExcelFile}
				onSave={saveRecurringOrders}
				onDelete={deleteRecurringOrders}
				onConfirmed={handleUploadConfirmed}
			/>
		</TableLayout>
	);
}
